import { useState } from "react";
import { useNavigate, useLocation, Navigate } from "react-router-dom";
import { Pizza, ShieldCheck, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { useAuth } from "../lib/auth";
import api from "../lib/api";

export default function Verify2FA() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { verify2fa } = useAuth();
  const initial = location.state || {};
  const [challengeToken, setChallengeToken] = useState(initial.challengeToken || "");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState("");

  if (!initial.challengeToken && !challengeToken) {
    return <Navigate to="/login" replace />;
  }

  const submit = async (e) => {
    e?.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await verify2fa(challengeToken, code.trim());
      toast.success(t("login.welcomeToast", { name: u.full_name }));
      navigate(u.must_change_password ? "/cambiar-password" : "/dashboard");
    } catch (err) {
      const msg = err?.response?.data?.detail || t("common.error");
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    setResending(true);
    setError("");
    try {
      await api.post("/auth/2fa/resend", { challenge_token: challengeToken });
      toast.success(t("twofa.resent"));
    } catch (err) {
      const msg = err?.response?.data?.detail || t("common.error");
      toast.error(msg);
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-slate-50">
      <div className="w-full max-w-md bg-white border border-slate-200 rounded-2xl p-8 shadow-sm">
        <div className="flex items-center gap-2.5 mb-5">
          <div className="w-10 h-10 rounded-xl bg-[#FFDF00] flex items-center justify-center">
            <Pizza className="w-6 h-6 text-[#1A1A1A]" strokeWidth={2.5} />
          </div>
          <div className="font-display font-extrabold text-xl">Chizy Chiz</div>
        </div>

        <h2 className="font-display font-extrabold text-2xl text-[#1A1A1A] tracking-tight flex items-center gap-2">
          <ShieldCheck className="w-6 h-6 text-emerald-500" />
          {t("twofa.title")}
        </h2>
        <p className="text-slate-500 mt-1.5 text-sm">{t("twofa.subtitle")}</p>

        <form onSubmit={submit} className="mt-6 space-y-4">
          <div>
            <label className="text-xs font-bold text-[#1A1A1A] uppercase tracking-wider">{t("twofa.codeLabel")}</label>
            <input
              data-testid="twofa-code-input"
              type="text"
              inputMode="numeric"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              placeholder="000000"
              className="mt-1.5 w-full px-4 py-3 text-center text-2xl tracking-[0.5em] font-bold bg-white border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FFDF00] focus:border-[#FFDF00]"
              required
              autoFocus
            />
          </div>

          {error && (
            <div data-testid="twofa-error" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-4 py-2.5">
              {error}
            </div>
          )}

          <button
            data-testid="twofa-submit-btn"
            type="submit"
            disabled={loading || code.length !== 6}
            className="w-full btn-primary-chizy py-3.5 rounded-xl flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {loading ? t("common.saving") : (<>{t("twofa.verify")} <ArrowRight className="w-4 h-4" strokeWidth={2.5} /></>)}
          </button>
        </form>

        <div className="mt-5 text-center">
          <button
            data-testid="twofa-resend-btn"
            onClick={resend}
            disabled={resending}
            className="text-sm text-slate-500 hover:text-[#1A1A1A] underline decoration-[#FFDF00] decoration-2 underline-offset-2 disabled:opacity-50"
          >
            {resending ? t("common.saving") : t("twofa.resend")}
          </button>
          <div className="mt-3">
            <button
              data-testid="twofa-back-login"
              onClick={() => navigate("/login")}
              className="text-xs text-slate-400 hover:text-slate-600"
            >
              {t("twofa.backToLogin")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
